import {
    DeleteOutlined, PlusOutlined,
} from '@ant-design/icons';
import {
    Button, Checkbox, Col, Flex, Form, Input, Row, Tooltip,
} from 'antd';
import {
    Control, Controller, FieldArrayWithId, FieldErrors, FieldValues, UseFieldArrayAppend,
    UseFieldArrayRemove,
} from 'react-hook-form';

import { TextEditor } from './TextEditor';

type EditableFieldsArrayProps<T extends FieldValues = FieldValues> = {
    control: Control<T>;
    errors: FieldErrors<T>;
    fields: FieldArrayWithId<T, any, 'id'>[];
    remove: UseFieldArrayRemove;
    append: UseFieldArrayAppend<T>;
    loading?: boolean;
    label: string;
    valueFieldMaxLength?: number;
    instructionImageUpload?: (file: File) => Promise<{ url: string }>;
    onLoadDefaults?: () => void;
    bulkUpdateIndices?: Set<number>;
    onToggleBulkUpdate?: (index: number) => void;
};

export const EditableFieldsArray = <T extends FieldValues = FieldValues>({
    control,
    errors,
    fields,
    remove,
    append,
    loading = false,
    label,
    instructionImageUpload,
    onLoadDefaults,
    bulkUpdateIndices,
    onToggleBulkUpdate,
}: EditableFieldsArrayProps<T>) => {
    const fieldName = label === 'Розділи' ? 'instructions' : 'fields';
    const nameFieldLabel = label === 'Розділи' ? 'Назва розділу' : 'Назва поля';
    const valueFieldLabel = label === 'Розділи' ? 'Значення розділу' : 'Значення поля';
    const addButtonText = label === 'Розділи' ? 'Додати розділ' : 'Додати поле';

    return (
        <Form.Item label={label}>
            <Row gutter={[16, 16]}>
                {fields.map((field, index) => (
                    <Col key={field.id} span={24}>
                        <div style={{ border: '1px solid #d9d9d9', borderRadius: '8px', padding: '16px', height: '100%' }}>
                            <Flex gap="small" vertical>
                                <Flex justify="space-between" align="flex-start">
                                    <Controller
                                        name={`${fieldName}.${index}.name` as any}
                                        control={control}
                                        render={({ field: nameField }) => (
                                            <Form.Item
                                                label={nameFieldLabel}
                                                validateStatus={(errors[fieldName] as any)?.[index]?.name ? 'error' : ''}
                                                help={(errors[fieldName] as any)?.[index]?.name?.message}
                                                style={{ flex: 1, margin: 0, marginRight: 8 }}
                                            >
                                                <Input
                                                    {...nameField}
                                                    placeholder={`Введіть ${nameFieldLabel.toLowerCase()}`}
                                                    maxLength={255}
                                                    disabled={loading}
                                                />
                                            </Form.Item>
                                        )}
                                    />
                                    <Flex align="center" gap={8} style={{ marginTop: 30 }}>
                                        {bulkUpdateIndices !== undefined && onToggleBulkUpdate && (
                                            <Tooltip title="Змінити цей розділ у всіх скриптах проекту">
                                                <Checkbox
                                                    checked={bulkUpdateIndices.has(index)}
                                                    onChange={() => onToggleBulkUpdate(index)}
                                                    disabled={loading}
                                                >
                                                    У всіх скриптах
                                                </Checkbox>
                                            </Tooltip>
                                        )}
                                        <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => remove(index)}
                                            disabled={loading}
                                        />
                                    </Flex>
                                </Flex>
                                <Controller
                                    name={`${fieldName}.${index}.value` as any}
                                    control={control}
                                    render={({ field: valueField }) => (
                                        <Form.Item
                                            label={valueFieldLabel}
                                            validateStatus={(errors[fieldName] as any)?.[index]?.value ? 'error' : ''}
                                            help={(errors[fieldName] as any)?.[index]?.value?.message}
                                            style={{ margin: 0 }}
                                        >
                                            {/*<TextEditorWarning />*/}
                                            <TextEditor
                                                value={valueField.value || ''}
                                                onChange={valueField.onChange}
                                                disabled={loading}
                                                onImageUpload={fieldName === 'instructions' ? instructionImageUpload : undefined}
                                                editorMinHeightPx={label === 'Розділи' || label === 'Інструкції' ? 380 : 200}
                                            />
                                        </Form.Item>
                                    )}
                                />
                            </Flex>
                        </div>
                    </Col>
                ))}
                <Col span={24}>
                    <Flex gap={8}>
                        <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={() => append({ name: '', value: '' } as any)}
                            disabled={loading}
                            style={{ flex: 1 }}
                        >
                            {addButtonText}
                        </Button>
                        {onLoadDefaults && (
                            <Button
                                type="dashed"
                                onClick={onLoadDefaults}
                                disabled={loading}
                            >
                                Підвантажити стандартні
                            </Button>
                        )}
                    </Flex>
                </Col>
            </Row>
        </Form.Item>
    );
};
